import React, { createRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { enqueueSnackbar } from 'notistack';
import { Tooltip } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import AddIcon from '@mui/icons-material/Add';
import UploadJsonIcon from '@mui/icons-material/North';
import { useHistory } from 'react-router-dom';
import NotReadyIcon from '@mui/icons-material/DoNotDisturbAlt';
import CachedIcon from '@mui/icons-material/Cached';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PushPinIcon from '@mui/icons-material/PushPin';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import cx from 'classnames';

import { FOLDER_CONTENT_FILTER, NOTIFICATION, VIDEOFEED_STATUS, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { ReactComponent as TimelineIcon } from '../../../assets/icons/timeline.svg';
import { ReactComponent as MovieIcon } from '../../../assets/icons/movieCollection.svg';
import {
  archiveVideofeedOnly,
  bypassCacheForNextVideofeedsListFetch,
  getFolderById,
  postCreateEmptyTimeline,
  postRefreshVideofeedStatus,
  postVideofeedViewRequest,
  removeVideofeedThumbnail,
  updateVideofeedTitle,
  uploadVideofeedThumbnail,
} from '../../../api';
import useOnClickOutside from '../../../helpers/useOnClickOutside';
import { DateFormatter } from '../../../helpers/dateFormatter';
import StorageIcon from '../../../assets/icons/storage.svg';
import DeleteVideofeedModal from '../DeleteVideofeedModal';
import {
  refetchFeeds,
  updateFeedToMove,
  updateFeedTransferMode,
  updateFeedsToMove,
  updateFeedToShare,
  updateShowMoveFeedModal,
  updateShowShareModal,
} from '../../../store/videoFeedsList/videoFeedsListSlice';
import { MoreIcon } from '../../../assets/icons/threedots';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import TrashButton from '../../modules/TrashButton';
import ShareButton from '../../modules/ShareButton';
import ShareAccessButton from '../../modules/ShareAccessButton';
import { bytesToGB, bytesToMB, findSearchValue, secondsToHours } from '../../../helpers';
import MoveButton from '../../modules/MoveButton';
import EditButton from '../../modules/EditButton';
import { COLLABORATIVE_FOLDER_TYPE, VIDEOFEED } from '../../../enums/TYPES';
import { PATHS } from '../../../enums/PATHS';
import styles from './Videofeed.module.scss';
import Icon from './icon';
import NotReady from '../../../assets/icons/not-ready';
import {
  updateAdditionalAngleUploadFeed,
  updatePkgToDelete,
  UploadingStatus,
} from '../../../store/sharedStore/sharedStoreSlice';
import DownloadButton from '../../modules/DownloadButton';
import DownloadDataModal from '../../modules/VideoFileList/DownloadDataModal';
import ShareExternallyModal from '../../modules/VideoFileList/ShareExternallyModal';
import { convertSeconds } from '../../Timeline/utils';
import { RolesLowerCase } from '../../../enums/ROLES';
import { pinContent, unpinContent } from '../../../api/pinnedContentApi';
import { Portal } from '../../../helpers/bodyPortal';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../../modules/DateSortHeader';
import { SortOptions } from '../../../enums/SORT';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import UploadJsonModal from '../../VideoPage/UploadJsonModal';
import FileTypeThumbnail from '../../modules/FileTypeThumbnail';
import CreateEmptyTimelineModal from '../../modules/CreateEmptyTimelineModal';
import ReplaceThumbnailModal from '../../modules/ReplaceThumbnailModal';
import ThumbnailButton from '../../modules/ThumbnailButton';

type TVideofeed = {
  videofeed: VIDEOFEED;
  fetchAll?: () => void;
  feedIndex?: number;
  loading?: boolean;
  showOwnerColumn?: boolean;
  referenceTimelines?: VIDEOFEED[];
  feedIndexById?: Record<string, number>;
  isReferenceTimelineRow?: boolean;
  embedded?: boolean;
  forceMovieIcon?: boolean;
  forceBlueTimelineIcon?: boolean;
  referenceIndex?: number;
  selectMode?: boolean;
  selectedReferenceKeys?: Set<string>;
  onReferenceSelectionChange?: (timeline: VIDEOFEED) => void;
  pinContextType?: string;
  pinContextParentId?: string | number | null;
  onPinUpdated?: () => void;
  allowAdditionalAngleUpload?: boolean;
  dateSortOption?: SortOptions | null;
};

const REFRESH_STATUS_COOLDOWN_MS = 30_000;
const refreshStatusLastTriggeredAtByFeedId = new Map<string, number>();
const ARCHIVE_ONLY_TEST_HOSTS = new Set([
  'dev.amplify.site',
  'sydney.amplify.site',
  'staging-london.amplify.site',
]);
const PROCESSING_STATUSES = new Set<string>([
  VIDEOFEED_STATUS.TRANSCODING,
  VIDEOFEED_STATUS.METADATA_UPLOADING,
  VIDEOFEED_STATUS.METADATA_PROCESSING,
]);
const RESUME_UPLOAD_CLEANUP_STATUSES = new Set<string>([VIDEOFEED_STATUS.TRANSCODING, VIDEOFEED_STATUS.DONE]);
const getRefreshStatusRemainingMs = (feedId?: string | number): number => {
  if (!feedId) return 0;
  const lastTriggeredAt = refreshStatusLastTriggeredAtByFeedId.get(String(feedId)) || 0;
  if (!lastTriggeredAt) return 0;
  return Math.max(0, REFRESH_STATUS_COOLDOWN_MS - (Date.now() - lastTriggeredAt));
};

const Videofeed: React.FC<TVideofeed> = ({
  videofeed,
  fetchAll,
  feedIndex = 0,
  loading,
  showOwnerColumn = false,
  referenceTimelines,
  feedIndexById,
  isReferenceTimelineRow = false,
  embedded = false,
  forceBlueTimelineIcon = false,
  referenceIndex,
  selectMode = false,
  selectedReferenceKeys,
  onReferenceSelectionChange,
  pinContextType,
  pinContextParentId,
  onPinUpdated,
  allowAdditionalAngleUpload = false,
  dateSortOption,
}) => {
  const hasTopicRows = (videofeed?.topics?.length || 0) > 0;
  const isTopLevelTimeline =
    videofeed?.type === VIDEOFEED_TYPE.TIMELINE &&
    (videofeed?.parent_videofeed_id === null || videofeed?.parent_videofeed_id === undefined);
  const isLegacyTopLevelTimelineCollection = isTopLevelTimeline && hasTopicRows;
  const normalizedReferenceTimelines = Array.isArray(referenceTimelines) ? referenceTimelines : [];
  const displayReferenceTimelines =
    normalizedReferenceTimelines.length > 0
      ? normalizedReferenceTimelines
      : !isReferenceTimelineRow && !embedded && isLegacyTopLevelTimelineCollection
        ? [videofeed]
        : [];
  const uniqueCollectionTimelineIds = new Set(displayReferenceTimelines.map((timeline) => String(timeline?.id || '')).filter(Boolean));
  const hasCollectionRootMetadata =
    Boolean(videofeed?.is_collection_root) || (!videofeed?.parent_videofeed_id && Number(videofeed?.movie_collection_id || 0) > 0);
  if (uniqueCollectionTimelineIds.size === 0 && hasCollectionRootMetadata) {
    uniqueCollectionTimelineIds.add(String(videofeed.id));
  }
  const collectionTimelineCount = uniqueCollectionTimelineIds.size;
  const hasCollectionReferenceTimelines = collectionTimelineCount > 1 || Boolean(videofeed?.has_reference_timelines);
  const hasReferencesSection = collectionTimelineCount > 0;
  const isCollectionRow =
    !isReferenceTimelineRow &&
    !embedded &&
    (hasCollectionRootMetadata || hasReferencesSection || Boolean(videofeed?.has_reference_timelines) || isLegacyTopLevelTimelineCollection);
  const isCollectionTimelineRow = embedded && isReferenceTimelineRow;
  const rawSizeBytes = Number(videofeed?.size || 0);
  const displaySizeBytes = isCollectionTimelineRow ? 0 : rawSizeBytes;
  const shouldShowSize = isCollectionTimelineRow || rawSizeBytes > 0;
  const shouldShowRefDuration = isCollectionTimelineRow || Boolean(videofeed?.duplicate);
  const isStandaloneVideoCopy =
    Boolean(videofeed?.duplicate) &&
    !hasTopicRows &&
    !isCollectionRow &&
    !isCollectionTimelineRow;
  const isProcessingStatus = PROCESSING_STATUSES.has(String(videofeed?.status || ''));
  const hasProcessingReferenceTimelines = displayReferenceTimelines.some(
    (timeline) => PROCESSING_STATUSES.has(String(timeline?.status || '')),
  );
  const canShowRefreshStatusAction =
    isProcessingStatus || (isCollectionRow && hasProcessingReferenceTimelines);
  const hasTimelineMarkers =
    !isStandaloneVideoCopy &&
    (Boolean(videofeed?.duplicate) || hasTopicRows);
  const deleteTargetLabel: 'timeline' | 'video' | 'composer' =
    videofeed?.type === VIDEOFEED_TYPE.COMPOSER || videofeed?.type === VIDEOFEED_TYPE.MY_COMPOSER
      ? 'composer'
      : hasTimelineMarkers
        ? 'timeline'
        : 'video';
  const sourceTitle = isCollectionRow ? videofeed.collection_name || videofeed.title : videofeed.title;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCollectionMode, setDeleteCollectionMode] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showExternalShareModal, setShowExternalShareModal] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [showCreateEmptyTimelineModal, setShowCreateEmptyTimelineModal] = useState(false);
  const [creatingEmptyTimeline, setCreatingEmptyTimeline] = useState(false);
  const [archiveOnlyLoading, setArchiveOnlyLoading] = useState(false);
  const [isCommandPressed, setIsCommandPressed] = useState(false);
  const [refreshStatusLoading, setRefreshStatusLoading] = useState(false);
  const [refreshStatusCooldownMs, setRefreshStatusCooldownMs] = useState(() => getRefreshStatusRemainingMs(videofeed?.id));
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [title, setTitle] = useState(sourceTitle);
  const isPinned = Boolean((videofeed as any)?.is_pinned);

  const { currentUser, myOrganization } = useAppSelector((state) => state.user);
  const { uploadingContentState } = useAppSelector((state) => state.sharedStore);
  const relatedFeedIds = new Set([
    String(videofeed?.id),
    ...displayReferenceTimelines.map((feed) => String(feed?.id)),
  ]);
  const pendingAdditionalAngleUploads = uploadingContentState.filter(
    (item) =>
      relatedFeedIds.has(String(item?.videofeedId)) &&
      item?.uploadMode === 'additional-angle' &&
      item?.status !== UploadingStatus.FAILED &&
      item?.status !== UploadingStatus.FINISHED,
  );
  const hasPendingAdditionalAngleUpload = pendingAdditionalAngleUploads.length > 0;
  const hasUploadingAdditionalAngle = pendingAdditionalAngleUploads.some(
    (item) => item.status === UploadingStatus.UPLOADING,
  );
  const collectionVideos = [videofeed, ...displayReferenceTimelines].flatMap(
    (feed) => feed?.videos || [],
  );
  const hasPlayableExistingAngle = collectionVideos.some(
    (video) => String((video as any)?.status || '').toUpperCase() === 'TRANSCODED',
  );
  const existingAngleCount = Math.max(
    videofeed?.videos?.length || 0,
    ...displayReferenceTimelines.map((feed) => feed?.videos?.length || 0),
  );
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ x: number; y: number } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const history = useHistory();
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const folderId = findSearchValue('folderId');
  const folderTrail = findSearchValue('folderTrail');
  const contentFilter = findSearchValue('contentFilter');
  const [loader, setLoader] = useState(false);
  const [customThumbnailUrl, setCustomThumbnailUrl] = useState(videofeed.thumbnail_url || '');
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const previewUrl = customThumbnailUrl || videofeed?.videos?.[0]?.s3_preview_url || '';

  const [referencesExpanded, setReferencesExpanded] = useState(hasReferencesSection);
  const showExpandedReferences = hasReferencesSection && referencesExpanded && !embedded;
  const isReferenceChildRow = embedded && isReferenceTimelineRow;
  const shellCollectionTarget =
    !embedded &&
    isCollectionRow &&
    !hasTopicRows &&
    displayReferenceTimelines.length > 0
      ? displayReferenceTimelines[0]
      : null;
  const shellCollectionTargetFeedIndex = shellCollectionTarget
    ? feedIndexById?.[String(shellCollectionTarget.id)] ?? feedIndex
    : feedIndex;

  useEffect(() => {
    setTitle(sourceTitle);
  }, [sourceTitle]);

  useEffect(() => {
    setCustomThumbnailUrl(videofeed.thumbnail_url || '');
  }, [videofeed.thumbnail_url]);

  useEffect(() => {
    setReferencesExpanded((prev) =>
      hasReferencesSection ? prev || hasReferencesSection : false,
    );
  }, [hasReferencesSection]);

  useEffect(() => {
    setRefreshStatusCooldownMs(getRefreshStatusRemainingMs(videofeed?.id));
  }, [videofeed?.id]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey) {
        setIsCommandPressed(true);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (!event.metaKey) {
        setIsCommandPressed(false);
      }
    };

    const resetCommandState = () => {
      setIsCommandPressed(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', resetCommandState);
    document.addEventListener('visibilitychange', resetCommandState);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', resetCommandState);
      document.removeEventListener('visibilitychange', resetCommandState);
    };
  }, []);

  useEffect(() => {
    if (!showDotsMenu || !isProcessingStatus) {
      return;
    }

    const updateCooldown = () => {
      const nextRemainingMs = getRefreshStatusRemainingMs(videofeed?.id);
      setRefreshStatusCooldownMs(nextRemainingMs);
      return nextRemainingMs;
    };

    if (updateCooldown() <= 0) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (updateCooldown() <= 0) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [showDotsMenu, videofeed?.id, videofeed?.status, isProcessingStatus]);

  const handleLocalStorageKeysForResumeUploading = () => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        if (
          key.startsWith('=videofeedToResume=') &&
          localStorage &&
          localStorage.getItem(key) &&
          localStorage.getItem(key)?.includes(videofeed.id)
        ) {
          const pkg = JSON.parse(localStorage.getItem(key)!);
          if (pkg && pkg?.id === videofeed?.id) {
            localStorage.removeItem(key);
            dispatch(updatePkgToDelete(videofeed));
          }
        }
      }
    }
  };

  useEffect(() => {
    if (RESUME_UPLOAD_CLEANUP_STATUSES.has(String(videofeed?.status || ''))) {
      handleLocalStorageKeysForResumeUploading();
    }
  }, [videofeed, isProcessingStatus]);

  const handleDeleteClick = (e: any, deleteCollection = false) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteCollectionMode(deleteCollection);
    setShowDeleteModal(true);
  };

  const onDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteCollectionMode(false);
  };

  type CustomEventHandler = React.MouseEvent<HTMLDivElement> | React.MouseEvent<SVGSVGElement>;
  const redirectToFeedPage = (e: CustomEventHandler, targetFeed: VIDEOFEED, targetFeedIndex: number) => {
    if (isEditing) return;
    if (loading) return;
    if (selectMode) return;
    if (
      targetFeed?.status &&
      targetFeed.status !== VIDEOFEED_STATUS.DONE &&
      !hasPendingAdditionalAngleUpload &&
      !(isProcessingStatus && hasPlayableExistingAngle)
    ) {
      return;
    }
    const parentPage = findSearchValue('parentPage');

    if (e.currentTarget.contains(e.target as Node) || (e.target as HTMLElement).tagName === 'path') {
      postVideofeedViewRequest(targetFeed.id);
      switch (targetFeed.type) {
        case VIDEOFEED_TYPE.COMPOSER:
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${targetFeed.id}&folderId=${folderId}&fileside=true&feed=${targetFeedIndex}&clipId=${targetFeed.videos[0].id}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.COMPOSER}${
              folderTrail ? `&folderTrail=${folderTrail}` : ''
            }${
              contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
                ? `&contentFilter=${contentFilter}`
                : ''
            }`,
          );
          break;
        case VIDEOFEED_TYPE.MY_COMPOSER:
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${targetFeed.id}&fileside=true&feed=${targetFeedIndex}&clipId=${targetFeed.videos[0].id}&clip=0&parentPage=${parentPage}&pageType=${VIDEOFEED_TYPE.MY_COMPOSER}${
              folderTrail ? `&folderTrail=${folderTrail}` : ''
            }${
              contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
                ? `&contentFilter=${contentFilter}`
                : ''
            }`,
          );
          break;
        default:
          history.push(
            `${PATHS.TIMELINE.path}?feedId=${targetFeed.id}&folderId=${folderId}&fileside=true&feed=${targetFeedIndex}&clipId=${targetFeed.videos[0].id}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.TIMELINE}${
              folderTrail ? `&folderTrail=${folderTrail}` : ''
            }${
              contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
                ? `&contentFilter=${contentFilter}`
                : ''
            }`,
          );
      }
    }
  };

  const redirectToVideoPage = (e: CustomEventHandler) => {
    if (shellCollectionTarget) {
      redirectToFeedPage(e, shellCollectionTarget, shellCollectionTargetFeedIndex);
      return;
    }
    redirectToFeedPage(e, videofeed, feedIndex);
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  const enableFeedNameEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
    }
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };
  const handleEdit = async (e: any) => {
    e.preventDefault();
    if (title.trim().length === 0) {
      setTitle(sourceTitle);
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);
      return;
    }

    setIsEditing(false);
    await updateVideofeedTitle(videofeed.id, title, {
      renameCollectionOnly: isCollectionRow,
    });
    dispatch(refetchFeeds());
  };

  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLDivElement | null>(null);
  const alignActionMenu = useCallback(() => {
    if (!showDotsMenu || !actionMenuRef.current) return;
    const menuWidth = actionMenuRef.current.offsetWidth || 210;
    const menuHeight = actionMenuRef.current.offsetHeight || 300;
    const viewportPadding = 12;
    const footerHeight = 52;
    if (menuAnchorPosition) {
      const x = Math.min(
        Math.max(menuAnchorPosition.x, viewportPadding),
        window.innerWidth - menuWidth - viewportPadding,
      );
      const spaceBelow = window.innerHeight - footerHeight - viewportPadding - menuAnchorPosition.y;
      const y =
        spaceBelow >= menuHeight
          ? menuAnchorPosition.y
          : Math.max(viewportPadding, menuAnchorPosition.y - menuHeight - 8);
      setMenuPosition((prev) => (prev.x === x && prev.y === y ? prev : { x, y }));
      return;
    }

    const triggerRect = menuTriggerRef.current?.getBoundingClientRect();
    if (!triggerRect) return;
    const x = Math.min(
      Math.max(triggerRect.right - menuWidth, viewportPadding),
      window.innerWidth - menuWidth - viewportPadding,
    );
    const spaceBelow = window.innerHeight - footerHeight - viewportPadding - triggerRect.bottom;
    const y =
      spaceBelow >= menuHeight
        ? triggerRect.bottom + 8
        : Math.max(viewportPadding, triggerRect.top - menuHeight - 8);

    setMenuPosition((prev) => (prev.x === x && prev.y === y ? prev : { x, y }));
  }, [menuAnchorPosition, showDotsMenu]);

  useLayoutEffect(() => {
    alignActionMenu();
  }, [alignActionMenu, showDotsMenu]);

  useEffect(() => {
    if (!showDotsMenu) return;
    const handleReposition = () => alignActionMenu();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [showDotsMenu, alignActionMenu]);

  const [parentFolder, setParentFolder] = useState<COLLABORATIVE_FOLDER_TYPE | null>(null);

  const fetchParentFolder = async () => {
    setLoader(true);
    try {
      const res = await getFolderById(folderId!);
      if (res.data) {
        setParentFolder(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  const isCollaborator =
    parentFolder?.collaborators.some((u) => u.email === currentUser?.email) ||
    !!parentFolder?.group_collaborators.find((g) => g.analysts.find((u) => u.email === currentUser?.email));
  const currentOrgUser =
    currentUser?.organization_users?.find((orgUser) => orgUser.organization?.id === myOrganization?.id) ||
    currentUser?.organization_users?.[0];
  const organizationHaveDownloadHours = Number(myOrganization?.download_hours_limit || 0) > 0;
  const allowDownloadOwnComposers = currentOrgUser?.allow_download_own_composers ?? true;
  const isViewerOrAnalyst = [RolesLowerCase.VIEWER, RolesLowerCase.ANALYST].includes(currentUser?.role?.title as RolesLowerCase);
  const isOwnMyComposer = videofeed?.type === VIDEOFEED_TYPE.MY_COMPOSER && videofeed?.created_by?.id === currentUser?.id;
  const canDownloadOwnMyComposer = !isViewerOrAnalyst || allowDownloadOwnComposers;
  const canShowMyComposerDownload = videofeed?.type !== VIDEOFEED_TYPE.MY_COMPOSER || organizationHaveDownloadHours;
  const canDownload =
    canShowMyComposerDownload &&
    (videofeed.highest_user_access?.allow_downloads ||
      isCollaborator ||
      (videofeed.created_by.id == currentUser?.id && (!isOwnMyComposer || canDownloadOwnMyComposer)));
  const canShareExternally =
    canDownload &&
    videofeed?.type !== VIDEOFEED_TYPE.MY_COMPOSER &&
    videofeed?.status === VIDEOFEED_STATUS.DONE;
  const canUploadAdditionalAngles =
    allowAdditionalAngleUpload &&
    !isReferenceTimelineRow &&
    !embedded &&
    videofeed?.type !== VIDEOFEED_TYPE.COMPOSER &&
    videofeed?.type !== VIDEOFEED_TYPE.MY_COMPOSER &&
    (videofeed?.status === VIDEOFEED_STATUS.DONE || hasPendingAdditionalAngleUpload) &&
    (existingAngleCount +
      pendingAdditionalAngleUploads.filter(
        (item) => !item.videoId || !collectionVideos.some((video) => String(video.id) === String(item.videoId)),
      ).length <
      4 ||
      hasUploadingAdditionalAngle);
  const canManageReferenceTimelines =
    videofeed?.created_by?.id === currentUser?.id ||
    isCollaborator ||
    videofeed?.highest_user_access?.access?.title === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE;
  const canUploadJson =
    (isCollectionRow || isTopLevelTimeline) &&
    !isReferenceTimelineRow &&
    !embedded &&
    videofeed?.status === VIDEOFEED_STATUS.DONE &&
    canManageReferenceTimelines;
  const canCreateEmptyTimeline =
    (isCollectionRow || isTopLevelTimeline) &&
    !isReferenceTimelineRow &&
    !embedded &&
    videofeed?.status === VIDEOFEED_STATUS.DONE &&
    canManageReferenceTimelines;
  useEffect(() => {
    if (folderId && !parentFolder && showDotsMenu && !loader) {
      fetchParentFolder();
    }
  }, [folderId, parentFolder, showDotsMenu, loader]);

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectMode) {
      return;
    }
    if (showDotsMenu) {
      setShowDotsMenu(false);
      setMenuAnchorPosition(null);
      return;
    }

    if (e.type === 'contextmenu') {
      setMenuAnchorPosition({ x: e.clientX, y: e.clientY });
    } else {
      setMenuAnchorPosition(null);
    }
    setShowDotsMenu(true);
  };

  const onReplaceThumbnail = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowThumbnailModal(true);
  };

  const switchDownloadDataModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const onDownload = (e: any) => {
    e.stopPropagation();
    setShowDownloadModal(true);
  };

  const onShareExternally = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowExternalShareModal(true);
    setShowDotsMenu(false);
  };

  const onUploadAdditionalAngles = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(updateAdditionalAngleUploadFeed(videofeed));
    setShowDotsMenu(false);
  };

  const onUploadJson = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowJsonModal(true);
  };

  const openCreateEmptyTimelineModal = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowCreateEmptyTimelineModal(true);
  };

  const onCreateEmptyTimeline = async (timelineName: string) => {
    if (creatingEmptyTimeline) return;

    setCreatingEmptyTimeline(true);
    try {
      await postCreateEmptyTimeline(videofeed.id, timelineName, { allContent: findSearchValue('allContent') === 'true' });
      bypassCacheForNextVideofeedsListFetch();
      if (fetchAll) {
        await fetchAll();
      } else {
        dispatch(refetchFeeds());
      }
      setReferencesExpanded(true);
      setShowCreateEmptyTimelineModal(false);
      enqueueSnackbar('Empty timeline created', { variant: 'success' });
    } catch (error: any) {
      notification.showMessage(error?.response?.data?.message || 'Failed to create empty timeline');
    } finally {
      setCreatingEmptyTimeline(false);
    }
  };

  const onRefreshStatus = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (refreshStatusLoading) return;
    const remainingMs = getRefreshStatusRemainingMs(videofeed?.id);
    if (remainingMs > 0) {
      setRefreshStatusCooldownMs(remainingMs);
      return;
    }

    setRefreshStatusLoading(true);
    refreshStatusLastTriggeredAtByFeedId.set(String(videofeed.id), Date.now());
    setRefreshStatusCooldownMs(REFRESH_STATUS_COOLDOWN_MS);
    try {
      const refreshIds = new Set<string>();
      const appendRefreshId = (id?: string | number, status?: string) => {
        if (!id) return;
        if (!PROCESSING_STATUSES.has(String(status || ''))) return;
        refreshIds.add(String(id));
      };

      appendRefreshId(videofeed.id, videofeed.status);
      if (isCollectionRow) {
        displayReferenceTimelines.forEach((timeline) =>
          appendRefreshId(timeline?.id, timeline?.status),
        );
      }

      if (!refreshIds.size) {
        setShowDotsMenu(false);
        setRefreshStatusLoading(false);
        return;
      }

      await Promise.allSettled(
        Array.from(refreshIds).map((id) => postRefreshVideofeedStatus(id)),
      );
      bypassCacheForNextVideofeedsListFetch();
      dispatch(refetchFeeds());
      enqueueSnackbar('Status refreshed', { variant: 'success' });
    } catch (err) {
      console.error(err);
      notification.showMessage('Failed to refresh status');
    } finally {
      setRefreshStatusLoading(false);
      setShowDotsMenu(false);
    }
  };

  const onArchiveOnlyClick = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      typeof window === 'undefined' ||
      !ARCHIVE_ONLY_TEST_HOSTS.has(window.location.hostname) ||
      !(e?.metaKey || isCommandPressed)
    ) {
      return;
    }
    if (archiveOnlyLoading) return;

    setArchiveOnlyLoading(true);
    try {
      await archiveVideofeedOnly(videofeed.id);
      bypassCacheForNextVideofeedsListFetch();
      dispatch(refetchFeeds());
      fetchAll?.();
      notification.showMessage('Videofeed archived');
      setShowDotsMenu(false);
    } catch (err) {
      console.error(err);
      notification.showMessage('Failed to archive videofeed');
    } finally {
      setArchiveOnlyLoading(false);
    }
  };

  const refreshStatusSecondsLeft = Math.ceil(refreshStatusCooldownMs / 1000);
  const isRefreshStatusCoolingDown = refreshStatusSecondsLeft > 0;
  const isRefreshStatusActionDisabled = refreshStatusLoading || isRefreshStatusCoolingDown;
  const refreshStatusTooltipTitle = isRefreshStatusCoolingDown
    ? `Wait for ${refreshStatusSecondsLeft}s to make another request`
    : 'Refresh status now';
  const canShowArchiveOnlyAction =
    !isCollectionRow &&
    !isReferenceTimelineRow &&
    typeof window !== 'undefined' &&
    ARCHIVE_ONLY_TEST_HOSTS.has(window.location.hostname) &&
    isCommandPressed;

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
    setMenuAnchorPosition(null);
  });

  const onMoveClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (isReferenceTimelineRow || (videofeed?.parent_videofeed_id !== null && videofeed?.parent_videofeed_id !== undefined)) {
      return;
    }
    dispatch(updateFeedToMove(videofeed));
    dispatch(updateFeedsToMove([videofeed]));
    dispatch(updateFeedTransferMode('move'));
    dispatch(updateShowMoveFeedModal(true));
    setShowDotsMenu(false);
  };

  const onCopyClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (videofeed?.type !== VIDEOFEED_TYPE.TIMELINE) {
      return;
    }
    const feedToCopy = {
      ...(videofeed as any),
      copy_scope: isCollectionRow ? 'collection-video' : 'timeline',
    } as VIDEOFEED;
    dispatch(updateFeedToMove(feedToCopy));
    dispatch(updateFeedsToMove([feedToCopy]));
    dispatch(updateFeedTransferMode('copy'));
    dispatch(updateShowMoveFeedModal(true));
    setShowDotsMenu(false);
  };

  const onShareClick = async (e: any, shareScope?: 'single' | 'collection') => {
    e.preventDefault();
    e.stopPropagation();
    const isCollectionShareTarget = isCollectionRow;
    dispatch(
      updateFeedToShare({
        ...(videofeed as any),
        share_scope: shareScope || (isCollectionShareTarget ? 'collection' : 'single'),
      }),
    );
    dispatch(updateShowShareModal(true));
    setShowDotsMenu(false);
  };

  const onPinToggle = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!pinContextType) return;
    try {
      if (isPinned) {
        await unpinContent({
          targetType: 'videofeed',
          targetId: videofeed.id,
          contextType: pinContextType,
          contextParentId: pinContextParentId,
        });
      } else {
        await pinContent({
          targetType: 'videofeed',
          targetId: videofeed.id,
          contextType: pinContextType,
          contextParentId: pinContextParentId,
        });
      }
      onPinUpdated?.();
      notification.showMessage(isPinned ? 'Unpinned' : 'Pinned');
      setShowDotsMenu(false);
    } catch {
      notification.showMessage('Failed to update pin');
    }
  };

  const parentRef = createRef<HTMLDivElement>();

  return (
    <div
      data-videofeed-id={videofeed.id}
      className={cx(
        styles.rowWrapper,
        embedded && styles.embeddedRowWrapper,
        isPinned && !embedded && styles.pinnedCollection,
      )}
    >
      <div
        ref={parentRef}
        className={cx(
          styles.container,
          showDotsMenu && styles.selectedVideofeed,
          embedded && styles.embeddedContainer,
          showExpandedReferences && styles.parentExpanded,
          isReferenceChildRow && styles.referenceChild,
          isReferenceChildRow && referenceIndex === 0 && styles.referenceChildFirst,
        )}
        onClick={redirectToVideoPage}
        onContextMenu={switchDotsMenu}
        style={{ position: 'relative' }}
      >
        <div className={styles.link} onClick={redirectToVideoPage}>
          <div className={styles.leftSide} onClick={redirectToVideoPage}>
            <FileTypeThumbnail
              className={isCollectionTimelineRow ? undefined : styles.fileTypeThumbnail}
              iconOnly={isCollectionTimelineRow}
              icon={
                <Icon onClick={redirectToVideoPage}>
                  {!isProcessingStatus && videofeed.status !== VIDEOFEED_STATUS.DONE ? (
                    <Tooltip title={'Video is not ready'}>
                      <div>
                        <NotReadyIcon className={styles.notReadyIcon} />
                      </div>
                    </Tooltip>
                  ) : videofeed?.type === VIDEOFEED_TYPE.MY_COMPOSER || videofeed?.type === VIDEOFEED_TYPE.COMPOSER ? (
                    <ComposerIcon className={styles.contentTypeIcon} style={{ pointerEvents: 'none' }} />
                  ) : forceBlueTimelineIcon || isReferenceTimelineRow ? (
                    <TimelineIcon
                      className={cx(styles.contentTypeIcon, isReferenceTimelineRow && styles.collectionTimelineIcon)}
                      style={{ pointerEvents: 'none', color: '#39b3ea' }}
                    />
                  ) : (
                    <MovieIcon className={styles.contentTypeIcon} style={{ pointerEvents: 'none' }} />
                  )}
                </Icon>
              }
            >
              {videofeed.status === VIDEOFEED_STATUS.DONE && previewUrl ? (
                <img alt={'preview url'} className={styles.thumbnail} onClick={redirectToVideoPage} src={previewUrl} />
              ) : (
                <div className={styles.imgContainer}>
                  <NotReady className={styles.thumbnail} onClick={redirectToVideoPage} />
                  {isProcessingStatus && (
                    <Tooltip title="Processing">
                      <div className={styles.progress}>
                        <LinearProgress
                          sx={{
                            width: '100%',
                            [`&.${linearProgressClasses.colorPrimary}`]: {
                              backgroundColor: '#F5F5F5',
                            },
                            [`& .${linearProgressClasses.bar}`]: {
                              backgroundColor: '#6500AA',
                            },
                          }}
                        />
                      </div>
                    </Tooltip>
                  )}
                </div>
              )}
            </FileTypeThumbnail>
            <div />
            {isEditing ? (
              <form
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onSubmit={handleEdit}
              >
                <input ref={inputRef} className={styles.input} onChange={handleChange} value={title} onBlur={handleEdit} />
              </form>
            ) : (
              <div
                className={cx(
                  styles.name,
                  !isProcessingStatus && videofeed.status !== VIDEOFEED_STATUS.DONE && styles.redTitle,
                )}
              >
                {title}
              </div>
            )}
          </div>
        </div>
        <div className={cx(styles.rightSide, showOwnerColumn && styles.rightSideWithOwner)}>
          {showOwnerColumn && (
            <Tooltip title={videofeed?.created_by?.username || '-'}>
              <div className={styles.owner}>{videofeed?.created_by?.username || '-'}</div>
            </Tooltip>
          )}
          <div className={styles.share}>
            <ShareAccessButton targetId={videofeed.id} targetType="videofeed" onClick={onShareClick} />
          </div>
          <div className={styles.team}>
            {isProcessingStatus && <div className={styles.statusBlue}>Processing</div>}
            {videofeed.status === VIDEOFEED_STATUS.DONE && <div className={styles.statusGreen}>Uploaded</div>}
            {!isProcessingStatus && videofeed.status !== VIDEOFEED_STATUS.DONE && (
              <div className={styles.statusRed}>Draft</div>
            )}
          </div>
          <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(videofeed, dateSortOption), true)}`}>
            <div className={styles.date}>{DateFormatter(getDateColumnValue(videofeed, dateSortOption), true)}</div>
          </Tooltip>
          <div className={styles.size}>
            {shouldShowSize && (
              <>
                <img src={StorageIcon} alt="storage" />
                {bytesToGB(displaySizeBytes, 2) > 1 ? `${bytesToGB(displaySizeBytes, 2)} GB` : `${bytesToMB(displaySizeBytes, 0)} MB`}
              </>
            )}
          </div>

          <div className={styles.duration} style={{ fontSize: shouldShowRefDuration ? '10px' : '12px' }}>
            {videofeed?.duration && (
              <Tooltip title={convertSeconds(videofeed.duration, false, true)}>
                <div> {shouldShowRefDuration ? `REF ${secondsToHours(videofeed.duration)}` : secondsToHours(videofeed.duration)} </div>
              </Tooltip>
            )}
          </div>

          <div className={styles.dotsMenuBtn}>
            {hasReferencesSection && !embedded && (
              <div
                className={styles.expandIcon}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setReferencesExpanded((prev) => !prev);
                }}
              >
                {referencesExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </div>
            )}
            <div ref={menuTriggerRef} className={styles.dotIcon} onClick={switchDotsMenu}>
              <MoreIcon />
            </div>
          </div>
        </div>
        {showDeleteModal && (
          <DeleteVideofeedModal
            clearLocalStorageData={handleLocalStorageKeysForResumeUploading}
            switchModal={onDeleteModalClose}
            open={showDeleteModal}
            videofeedId={videofeed.id}
            duplicate={videofeed.duplicate}
            hasReferenceTimelines={deleteCollectionMode && hasCollectionReferenceTimelines}
            deleteCollection={deleteCollectionMode}
            deleteTargetLabel={deleteTargetLabel}
            fetchAll={fetchAll}
          />
        )}
        <Portal>
          <div
            ref={actionMenuRef}
            className={styles.actionMenu}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              display: showDotsMenu ? 'block' : 'none',
              top: `${menuPosition.y}px`,
              left: `${menuPosition.x}px`,
              right: 'auto',
            }}
          >
            {videofeed.status === VIDEOFEED_STATUS.DONE && (
              <>
                <div className={styles.menuOption} onClick={enableFeedNameEdit}>
                  <EditButton /> Rename
                </div>
                {!isReferenceTimelineRow && (videofeed?.parent_videofeed_id === null || videofeed?.parent_videofeed_id === undefined) && (
                  <div className={styles.menuOption} onClick={onMoveClick}>
                    <MoveButton /> Move to
                  </div>
                )}
                {videofeed.type === VIDEOFEED_TYPE.TIMELINE && (
                  <div className={styles.menuOption} onClick={onCopyClick}>
                    <span className={styles.refreshStatusIcon}>
                      <ContentCopyIcon fontSize="small" />
                    </span>
                    Copy to
                  </div>
                )}
              </>
            )}
            <div className={styles.menuOption} onClick={onShareClick}>
              <ShareButton /> Share
            </div>
            {canManageReferenceTimelines && !isReferenceTimelineRow && !embedded && videofeed.status === VIDEOFEED_STATUS.DONE && (
              <div className={styles.menuOption} onClick={onReplaceThumbnail}>
                <ThumbnailButton />
                Replace Thumbnail Image
              </div>
            )}
            {canShareExternally && (
              <div className={styles.menuOption} onClick={onShareExternally}>
                <ShareButton /> Share Externally
              </div>
            )}
            {canDownload && videofeed.status === VIDEOFEED_STATUS.DONE && (
              <div className={styles.menuOption} onClick={onDownload}>
                <DownloadButton /> Download
              </div>
            )}
            {canUploadAdditionalAngles && (
              <div className={styles.menuOption} onClick={onUploadAdditionalAngles}>
                <span className={styles.refreshStatusIcon}>
                  <AddIcon fontSize="small" style={{ width: 32 }} />
                </span>
                Upload additional Angle/s
              </div>
            )}
            {canUploadJson && (
              <div className={styles.menuOption} onClick={onUploadJson}>
                <span className={styles.refreshStatusIcon}>
                  <UploadJsonIcon fontSize="small" />
                </span>
                Upload Timeline Data
              </div>
            )}
            {canCreateEmptyTimeline && (
              <div className={styles.menuOption} onClick={openCreateEmptyTimelineModal}>
                <span className={styles.refreshStatusIcon}>
                  <AddIcon fontSize="small" />
                </span>
                {creatingEmptyTimeline ? 'Creating...' : 'Create Empty Timeline'}
              </div>
            )}
            {pinContextType && !isReferenceTimelineRow && !embedded && (
              <div className={styles.menuOption} onClick={onPinToggle}>
                <span style={{ display: 'inline-flex', width: 32, height: 30, padding: 4, borderRadius: 'var(--ui-border-radius)', background: 'var(--action-menu-icon-bg)', alignItems: 'center', justifyContent: 'center', marginRight: 12, boxSizing: 'border-box' }}>
                  <PushPinIcon sx={{ fontSize: 20, color: '#fff' }} />
                </span>
                {isPinned ? 'Unpin' : 'Pin'}
              </div>
            )}
            {canShowArchiveOnlyAction && (
              <div
                className={cx(styles.menuOption, archiveOnlyLoading && styles.menuOptionDisabled)}
                onClick={onArchiveOnlyClick}
                aria-disabled={archiveOnlyLoading}
              >
                <TrashButton />
                {archiveOnlyLoading ? 'Archiving...' : 'Archive only'}
              </div>
            )}
            {loader && <LinearProgress />}
            {isCollectionRow ? (
              <div className={styles.menuOption} onClick={(e) => handleDeleteClick(e, true)}>
                <TrashButton /> Delete collection
              </div>
            ) : (
              <div className={styles.menuOption} onClick={(e) => handleDeleteClick(e, false)}>
                <TrashButton /> {(isReferenceTimelineRow || Number(videofeed?.movie_collection_id || 0) > 0) ? 'Delete Timeline' : 'Delete'}
              </div>
            )}
            {canShowRefreshStatusAction && (
              <Tooltip title={refreshStatusTooltipTitle} disableHoverListener={!isRefreshStatusCoolingDown}>
                <div
                  className={cx(styles.menuOption, isRefreshStatusActionDisabled && styles.menuOptionDisabled)}
                  onClick={onRefreshStatus}
                  aria-disabled={isRefreshStatusActionDisabled}
                >
                  <span className={cx(styles.refreshStatusIcon, isRefreshStatusActionDisabled && styles.refreshStatusIconDisabled)}>
                    <CachedIcon fontSize="small" />
                  </span>
                  {refreshStatusLoading
                    ? 'Refreshing Status...'
                    : isRefreshStatusCoolingDown
                      ? `Refresh Status (${refreshStatusSecondsLeft}s)`
                      : 'Refresh Status'}
                </div>
              </Tooltip>
            )}
          </div>
        </Portal>
        <DownloadDataModal switchModal={switchDownloadDataModal} isOpen={showDownloadModal} feedId={videofeed.id} />
        <ShareExternallyModal
          switchModal={() => setShowExternalShareModal((prev) => !prev)}
          isOpen={showExternalShareModal}
          feedId={videofeed.id}
          referenceTimelines={displayReferenceTimelines}
        />
        <ReplaceThumbnailModal
          open={showThumbnailModal}
          setOpen={setShowThumbnailModal}
          onUpload={(data) => uploadVideofeedThumbnail(videofeed.id, data)}
          onUploaded={(updatedVideofeed) => setCustomThumbnailUrl(updatedVideofeed.thumbnail_url || '')}
          onRestore={customThumbnailUrl ? () => removeVideofeedThumbnail(videofeed.id) : undefined}
          preview={{ type: 'file', title, imageUrl: previewUrl }}
        />
        {showJsonModal && (
          <UploadJsonModal
            open
            switchModal={() => setShowJsonModal(false)}
            videoFeed={videofeed}
            fetchData={() => fetchAll?.()}
            onFinish={() => fetchAll?.()}
          />
        )}
        {showCreateEmptyTimelineModal && (
          <CreateEmptyTimelineModal
            open
            defaultName={videofeed.collection_name || videofeed.title}
            loading={creatingEmptyTimeline}
            onClose={() => setShowCreateEmptyTimelineModal(false)}
            onCreate={onCreateEmptyTimeline}
          />
        )}
      </div>

      {showExpandedReferences && (
        <div className={styles.referencesList}>
          {displayReferenceTimelines.map((timeline, index) => {
            const canSelectReference =
              selectMode &&
              timeline.type === VIDEOFEED_TYPE.TIMELINE &&
              (Number(timeline.movie_collection_id || 0) > 0 ||
                (timeline.parent_videofeed_id !== null &&
                  timeline.parent_videofeed_id !== undefined));
            const referenceKey = `reference:${timeline.id}`;

            return (
              <div
                key={`timeline-${timeline.id}`}
                className={styles.referenceSelectableRow}
              >
                {selectMode && (
                  <div className={styles.referenceSelectionCheckbox}>
                    {canSelectReference && (
                      <Checkbox
                        checked={Boolean(selectedReferenceKeys?.has(referenceKey))}
                        onChange={(e) => {
                          e.stopPropagation();
                          onReferenceSelectionChange?.(timeline);
                        }}
                        sx={{
                          color: 'rgba(255,255,255,0.6)',
                          '&.Mui-checked': {
                            color: '#39b3ea',
                          },
                        }}
                      />
                    )}
                  </div>
                )}
                <div
                  className={styles.referenceSelectableContent}
                  style={{ pointerEvents: selectMode ? 'none' : undefined }}
                >
                  <Videofeed
                    videofeed={timeline}
                    fetchAll={fetchAll}
                    feedIndex={feedIndexById?.[String(timeline.id)] ?? feedIndex}
                    loading={loading}
                    showOwnerColumn={showOwnerColumn}
                    isReferenceTimelineRow
                    embedded
                    forceBlueTimelineIcon
                    referenceIndex={index}
                    selectMode={selectMode}
                    pinContextType={pinContextType}
                    pinContextParentId={pinContextParentId}
                    onPinUpdated={onPinUpdated}
                    dateSortOption={dateSortOption}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Videofeed;
